// scriptParser.ts — Wrapper around lib/scriptParser.ts that dispatches to Redux store

import { store } from '../store';
import { setParsedLines, setCharacters } from '../store/scriptSlice';
import {
    classifyLine as _pureClassifyLine,
    type ScriptLine,
} from '../lib/scriptParser';

let sawFirstAllCapsHeading = false;

interface ApiParseResult {
    lines: ScriptLine[];
    characters: string[];
    script_text?: string;
}

function applyParseResult(result: ApiParseResult): boolean {
    store.dispatch(setParsedLines(result.lines));
    store.dispatch(setCharacters(result.characters));

    console.log('Parsed', result.lines.length, 'lines from', result.characters.length, 'characters');

    return result.lines.length > 0;
}

async function parseViaApi(endpoint: string, body: object): Promise<ApiParseResult> {
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const detail = await response.text();
        console.error(`Parse API failed at ${endpoint}`, response.status, detail);
        throw new Error(`Parse API failed: ${response.status}`);
    }

    const data = await response.json();
    return {
        lines: Array.isArray(data.lines) ? data.lines : [],
        characters: Array.isArray(data.characters) ? data.characters : [],
        script_text: typeof data.script_text === 'string' ? data.script_text : undefined,
    };
}

export async function parseScript(text: string, scriptName?: string): Promise<boolean> {
    const trimmed = String(text || '').trim();
    if (!trimmed) {
        return false;
    }

    const result = await parseViaApi('/api/parse-script', {
        text: trimmed,
        script_name: scriptName || null,
    });
    return applyParseResult(result);
}

export async function parseScriptPdf(file: File, scriptName?: string): Promise<{ ok: boolean; scriptText: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (scriptName) {
        formData.append('script_name', scriptName);
    }

    const response = await fetch('/api/parse-script-pdf', {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        const detail = await response.text();
        console.error('Parse API failed at /api/parse-script-pdf', response.status, detail);
        throw new Error(`Parse API failed: ${response.status}`);
    }

    const data = await response.json();
    const result: ApiParseResult = {
        lines: Array.isArray(data.lines) ? data.lines : [],
        characters: Array.isArray(data.characters) ? data.characters : [],
        script_text: typeof data.script_text === 'string' ? data.script_text : undefined,
    };

    return {
        ok: applyParseResult(result),
        scriptText: result.script_text || '',
    };
}

import type { ClassifyResult } from '../lib/scriptParser';

export function classifyLine(text: string, characterSet: Set<string> | string[] = []): ClassifyResult {
    const charSet = characterSet instanceof Set ? characterSet : new Set(characterSet);
    const result = _pureClassifyLine(text, charSet, sawFirstAllCapsHeading);
    sawFirstAllCapsHeading = result.sawFirstAllCapsHeading;
    return { type: result.type, characterGuess: result.characterGuess };
}
